<template>
  <v-autocomplete
    v-model="internalValue"
    :label="label"
    v-bind="$attrs"
    :items="getTimeZonesWithOffsets"
    :hide-details="hideDetails"
    :search="search"
    :custom-filter="customFilter"
    :return-object="false"
    item-value="timeZone"
    :item-title="getFormattedName"
  />
</template>

<script lang="ts">
import { PropType, defineComponent } from 'vue';

import { Nullable } from '@/utils/types';

interface IData {
  search: Nullable<string>;
}

export default defineComponent({
  name: 'TimeZoneSelect',

  props: {
    modelValue: {
      type: String as PropType<Nullable<string>>,
      default: null
    },
    label: {
      type: String,
      default: 'Timezone'
    },
    hideDetails: {
      type: Boolean,
      default: true
    }
  },

  emits: ['update:modelValue'],

  data: (): IData => ({
    search: null,
  }),

  computed: {
    internalValue: {
      get (): Nullable<string> {
        return this.modelValue;
      },
      set (value: Nullable<string>) {
        this.$emit('update:modelValue', value)
      }
    },
    getTimeZonesWithOffsets (): { timeZone: string; offset: string }[] {
      const timeZones = Intl.supportedValuesOf('timeZone');

      return timeZones.map(timeZone => {
        const now = new Date();
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone,
          hour12: false,
          timeZoneName: 'short'
        });
        const parts = formatter.formatToParts(now);
        const offset = parts.find(part => part.type === 'timeZoneName')?.value || '';

        return {
          timeZone,
          offset
        };
      });
    }
  },

  methods: {
    customFilter (value: string, queryText: Nullable<string>, item?: any): boolean {
      const text = queryText?.toLowerCase() || '';
      const raw: { timeZone: string; offset: string } | undefined = item?.raw;

      if (!raw) return false;

      return raw.timeZone.toLowerCase().indexOf(text) > -1 || raw.offset.toLowerCase().indexOf(text) > -1;
    },
    getFormattedName (item: { timeZone: string; offset: string }): string {
      return `${item.timeZone} (${item.offset})`;
    }
  }
})
</script>
